/**
 * Pure helpers for the Apprise deployment detail panel: polling, the two
 * independent tracks, server outputs (whitelisted — never any secret) and the
 * control-plane verification block.
 */
import type {
  AppTrackStatus, ApprisePlan, AppriseServerSummary, AppriseVmOutputs, AppriseVmVerify, DeploymentStatus,
  DeploymentTracks, InfraTrackStatus,
} from '@/types'
import type { PlanApprovalCopy } from '@/components/plan-approval/planApproval'
import { APPRISE_APP_NOT_CONFIGURED_MESSAGE } from '@/utils/apprise'
import { OS_LABELS } from './appriseReview'

export type TrackTone = 'muted' | 'running' | 'success' | 'error' | 'warning'

export const INFRA_TRACK_LABELS: Record<InfraTrackStatus, string> = {
  not_started: 'Not started',
  planning: 'Planning',
  awaiting_approval: 'Awaiting plan approval',
  applying: 'Applying',
  verifying: 'Verifying server',
  ready: 'Infrastructure ready',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

export const INFRA_TRACK_TONE: Record<InfraTrackStatus, TrackTone> = {
  not_started: 'muted',
  planning: 'running',
  awaiting_approval: 'warning',
  applying: 'running',
  verifying: 'running',
  ready: 'success',
  failed: 'error',
  cancelled: 'muted',
}

export const APP_TRACK_LABELS: Record<AppTrackStatus, string> = {
  not_configured: 'Not configured',
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
}

export const APP_TRACK_TONE: Record<AppTrackStatus, TrackTone> = {
  not_configured: 'muted',
  pending: 'muted',
  running: 'running',
  completed: 'success',
  failed: 'error',
}

/** Apprise statuses during which the detail should be polled (plan / apply / verify in flight). */
const POLL_STATUSES = new Set<string>([
  'pending', 'validating', 'planning_infra', 'awaiting_plan_approval', 'applying_infra', 'verifying_server',
])

export function shouldPollAppriseDetail(status: DeploymentStatus | string): boolean {
  return POLL_STATUSES.has(status)
}

const INFRA_STATUS_BY_DEPLOYMENT: Record<string, InfraTrackStatus> = {
  planning_infra: 'planning',
  awaiting_plan_approval: 'awaiting_approval',
  applying_infra: 'applying',
  verifying_server: 'verifying',
  infra_ready: 'ready',
  failed: 'failed',
  rolled_back: 'failed',
  cancelled: 'cancelled',
}

/**
 * Tracks from the deployment status — only a fallback for when the API did not
 * send them. In this scope the application track is ALWAYS "not configured".
 */
export function deriveTracks(status: DeploymentStatus | string): DeploymentTracks {
  return {
    infra: { status: INFRA_STATUS_BY_DEPLOYMENT[status] ?? 'not_started', run_url: '', message: '' },
    app: { status: 'not_configured', message: APPRISE_APP_NOT_CONFIGURED_MESSAGE },
  }
}

export function resolveTracks(
  fromDetail: DeploymentTracks | null | undefined,
  fromDeployment: DeploymentTracks | null | undefined,
  status: DeploymentStatus | string,
): DeploymentTracks {
  return fromDetail ?? fromDeployment ?? deriveTracks(status)
}

/** The Apprise application is never installed by this scope — never claim otherwise. */
export function appMessage(tracks: DeploymentTracks): string {
  return tracks.app.message || (tracks.app.status === 'not_configured' ? APPRISE_APP_NOT_CONFIGURED_MESSAGE : '')
}

export interface DetailRow {
  label: string
  value: string
  mono?: boolean
}

/**
 * Server outputs shown on the detail page. A whitelist: outputs never contain
 * secrets, but unknown keys are not displayed anyway (defence in depth).
 */
export function serverOutputRows(outputs: AppriseVmOutputs | null | undefined): DetailRow[] {
  if (!outputs) return []
  const o = outputs
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '')
  const rows: Array<DetailRow & { show: boolean }> = [
    { label: 'VM name', value: text(o.vm_name), mono: true, show: !!text(o.vm_name) },
    { label: 'Resource group', value: text(o.resource_group), mono: true, show: !!text(o.resource_group) },
    { label: 'Location', value: text(o.location), show: !!text(o.location) },
    { label: 'Hostname', value: text(o.hostname), mono: true, show: !!text(o.hostname) },
    { label: 'Operating system', value: OS_LABELS[text(o.os_type)] ?? text(o.os_type), show: !!text(o.os_type) },
    { label: 'VM size', value: text(o.vm_size), mono: true, show: !!text(o.vm_size) },
    { label: 'Private IP', value: text(o.private_ip) || '—', mono: true, show: true },
    { label: 'Public IP', value: text(o.public_ip) || 'None (private only)', mono: !!text(o.public_ip), show: true },
    { label: 'Access method', value: accessLabel(text(o.admin_access_method)), show: !!text(o.admin_access_method) },
    { label: 'Virtual network', value: text(o.vnet_name), mono: true, show: !!text(o.vnet_name) },
    { label: 'Subnet', value: text(o.subnet_name), mono: true, show: !!text(o.subnet_name) },
    { label: 'Network security group', value: text(o.nsg_name), mono: true, show: !!text(o.nsg_name) },
    { label: 'Key Vault', value: text(o.key_vault_name), mono: true, show: !!text(o.key_vault_name) },
    { label: 'Credential secret name', value: text(o.credential_secret_name), mono: true, show: !!text(o.credential_secret_name) },
    { label: 'Backup vault', value: text(o.backup_vault_name), mono: true, show: !!text(o.backup_vault_name) },
    { label: 'Log Analytics workspace', value: text(o.log_analytics_workspace_id), mono: true, show: !!text(o.log_analytics_workspace_id) },
  ]
  return rows.filter((r) => r.show).map(({ label, value, mono }) => ({ label, value, mono }))
}

function accessLabel(method: string): string {
  const labels: Record<string, string> = {
    managed_outside: 'Managed outside the portal', azure_bastion: 'Azure Bastion', private_network: 'Private network', jit: 'Just-in-time',
  }
  return labels[method] ?? method
}

/** Heading of the verification block — control plane only, never "reachable". */
export const VERIFY_HEADING = 'Verified through the Azure control plane'

export function verifyRows(verify: AppriseVmVerify | null | undefined): DetailRow[] {
  if (!verify) return []
  return [
    { label: 'Provisioning state', value: verify.provisioning_state || '—' },
    { label: 'Power state', value: verify.power_state || '—' },
    { label: 'VM agent status', value: verify.vm_agent_status || '—' },
    { label: 'Method', value: verify.method === 'azure_control_plane' ? 'Azure control plane' : (verify.method || '—') },
  ]
}

/** One-line server description for lists: name · OS · size · region · IP. */
export function serverSummaryParts(server: AppriseServerSummary | null | undefined): string[] {
  if (!server) return []
  const parts = [
    server.name,
    server.os_type ? (OS_LABELS[server.os_type] ?? server.os_type) : '',
    server.vm_size,
    server.region,
    server.private_ip ? `IP ${server.private_ip}` : '',
  ]
  return parts.filter(Boolean)
}

/** Apprise says "create the VM", not "terraform apply", wherever the wording is user-facing. */
export const APPRISE_PLAN_COPY: PlanApprovalCopy = {
  approveButton: 'Approve and create VM',
  confirmApproveButton: 'Yes, create VM',
  describeApprove: (counts) =>
    `Approving creates the virtual machine and its supporting Azure resources for exactly this plan (${counts}). The Apprise application is not installed.`,
}

/** Confirm text naming how many resources the plan creates. */
export function appriseConfirmCreateText(plan: Pick<ApprisePlan, 'plan_summary'>): string {
  const n = plan.plan_summary?.add
  if (n == null) return 'Confirm: create the Azure virtual machine from this plan?'
  return `Confirm: create the Azure virtual machine and ${n} resource${n === 1 ? '' : 's'} from this plan?`
}
